// The interior tutorial's state, shared by the practice room and the furniture
// shop: which step, which phase inside an item step (derived from the practice
// room's furniture — domain/interiorTutorial), and the ways out of it.
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isItemStep,
  isPracticeRoom,
  itemPhase,
  PRACTICE_ROOM_ID,
  TUTORIAL_DONE,
  TUTORIAL_ITEM,
  TUTORIAL_SKIPPED,
  type ItemPhase,
  type TutorialStep,
} from '../../../domain/interiorTutorial';
import type { HouseFurniture } from '../../../models/apiTypes';
import { selectionChanged } from '../../../utils/telegram/hapticFeedback';
import { advancePractice, endPractice, usePracticeRoom } from './practiceRoom';

export interface InteriorTutorial {
  active: boolean;
  step: TutorialStep | null;
  phase: ItemPhase | null;
  /** The step is done (or skipped as stuck) — on to the next one. */
  advance: () => void;
  /** Leave the tutorial for the real house (behind a confirm in the UI). */
  skip: () => void;
  /** The finish card's button: into the real house, tutorial done. */
  finish: () => void;
}

export function useInteriorTutorial(mapObjectId: number | null | undefined): InteriorTutorial {
  const queryClient = useQueryClient();
  const running = usePracticeRoom((s) => s.realHouseId !== null);
  const practiceStep = usePracticeRoom((s) => s.step);
  const active = running && isPracticeRoom(mapObjectId);
  // The practice room lives only in this cache entry — read it, never fetch it.
  const { data: items = [] } = useQuery<HouseFurniture[]>({
    queryKey: ['houseFurniture', PRACTICE_ROOM_ID],
    queryFn: () => queryClient.getQueryData<HouseFurniture[]>(['houseFurniture', PRACTICE_ROOM_ID]) ?? [],
    enabled: false,
  });

  const step = active ? practiceStep : null;
  const phase = isItemStep(step) ? itemPhase(items, TUTORIAL_ITEM[step]) : null;

  const advance = useCallback(() => {
    if (!step) return;
    advancePractice(step);
    selectionChanged();
  }, [step]);
  const skip = useCallback(() => endPractice(queryClient, TUTORIAL_SKIPPED), [queryClient]);
  const finish = useCallback(() => endPractice(queryClient, TUTORIAL_DONE), [queryClient]);

  return { active, step, phase, advance, skip, finish };
}
